/**
 * Premium Quality Scoring Engine — scores a composition 0–100.
 *
 * Seven weighted metrics: visual hierarchy, composition, balance, contrast,
 * typography, whitespace, alignment — plus a "premium feel" bonus derived
 * from the others. Each metric returns 0–1; the report exposes both the
 * aggregate score and the per-metric breakdown so the admin UI (and later
 * the AI feedback loop) can see *why* a template scored what it did.
 */

import { contrastRatio } from './colorHarmonyEngine';
import { alignmentConsistency } from './alignmentEngine';
import { rectOverlapArea } from './layoutEngine';

const clamp01 = (v) => Math.max(0, Math.min(1, v));

const WEIGHTS = {
  hierarchy: 0.18,
  composition: 0.16,
  balance: 0.14,
  contrast: 0.14,
  typography: 0.14,
  whitespace: 0.12,
  alignment: 0.12,
};

// ── Individual metrics (each: compose() result → 0..1) ──────────────────────

/** Hierarchy: the biggest text should be meaningfully bigger than body text. */
const scoreHierarchy = ({ commands }) => {
  const sizes = commands
    .filter((c) => c.params.fontSize)
    .map((c) => c.params.fontSize);
  if (sizes.length < 2) return 0.4;
  const ratio = Math.max(...sizes) / Math.min(...sizes);
  // Ratio of 3–6 reads as confident hierarchy; <1.5 reads flat.
  if (ratio >= 3 && ratio <= 6) return 1;
  if (ratio > 6) return clamp01(1 - (ratio - 6) / 8);
  return clamp01((ratio - 1) / 2);
};

/** Composition: required zones populated, decorations present but capped. */
const scoreComposition = ({ placed, decorations }) => {
  const zonesUsed = new Set(
    placed.filter((p) => !p.structural).map((p) => p.nodeType)
  );
  const coverage = clamp01(zonesUsed.size / 5);
  const decorationScore = decorations.length === 0 ? 0.6 : decorations.length <= 4 ? 1 : 0.7;
  return coverage * 0.7 + decorationScore * 0.3;
};

/** Balance: horizontal center of visual weight should sit near canvas center. */
const scoreBalance = ({ placed, layout }) => {
  const content = placed.filter((p) => !p.structural);
  if (!content.length) return 0;
  let weight = 0, moment = 0;
  content.forEach(({ rect }) => {
    const area = rect.width * rect.height;
    weight += area;
    moment += area * (rect.x + rect.width / 2);
  });
  const center = moment / weight;
  const offset = Math.abs(center - layout.canvas.width / 2) / (layout.canvas.width / 2);
  return clamp01(1 - offset * 2);
};

/** Contrast: palette text/background readability. */
const scoreContrast = ({ tokens }) => {
  const ratio = contrastRatio(tokens.palette.text, tokens.palette.background);
  if (ratio >= 7) return 1;
  if (ratio >= 4.5) return 0.85;
  if (ratio >= 3) return 0.6;
  return clamp01(ratio / 3) * 0.5;
};

/** Typography: few families, consistent scale steps. */
const scoreTypography = ({ commands }) => {
  const families = new Set(
    commands.filter((c) => c.params.fontFamily).map((c) => c.params.fontFamily)
  );
  if (!families.size) return 0.4;
  const familyScore = families.size <= 2 ? 1 : families.size === 3 ? 0.7 : 0.4;
  const sizes = [...new Set(
    commands.filter((c) => c.params.fontSize).map((c) => c.params.fontSize)
  )].sort((a, b) => a - b);
  let stepScore = 1;
  for (let i = 1; i < sizes.length; i++) {
    const step = sizes[i] / sizes[i - 1];
    if (step < 1.1 && sizes[i] !== sizes[i - 1]) stepScore -= 0.15; // muddy near-duplicates
  }
  return familyScore * 0.6 + clamp01(stepScore) * 0.4;
};

/** Whitespace: content should cover 25–60% of the canvas. */
const scoreWhitespace = ({ placed, layout }) => {
  const canvasArea = layout.canvas.width * layout.canvas.height;
  const content = placed.filter((p) => !p.structural);
  // Approximate union by summing minus pairwise overlaps.
  let covered = content.reduce((s, p) => s + p.rect.width * p.rect.height, 0);
  for (let i = 0; i < content.length; i++) {
    for (let j = i + 1; j < content.length; j++) {
      covered -= rectOverlapArea(content[i].rect, content[j].rect);
    }
  }
  const density = clamp01(covered / canvasArea);
  if (density >= 0.25 && density <= 0.6) return 1;
  if (density < 0.25) return clamp01(density / 0.25) * 0.8;
  return clamp01(1 - (density - 0.6) / 0.4);
};

/** Alignment: shared axes across content rects. */
const scoreAlignment = ({ placed }) =>
  alignmentConsistency(placed.filter((p) => !p.structural).map((p) => p.rect));

// ── Aggregate ────────────────────────────────────────────────────────────────

/**
 * Score a compose() result.
 * Returns { score: 0–100, premiumFeel: 0–100, breakdown: { metric: 0–100 } }.
 */
export const scoreComposition100 = (composition) => {
  const metrics = {
    hierarchy: scoreHierarchy(composition),
    composition: scoreComposition(composition),
    balance: scoreBalance(composition),
    contrast: scoreContrast(composition),
    typography: scoreTypography(composition),
    whitespace: scoreWhitespace(composition),
    alignment: scoreAlignment(composition),
  };

  const weighted = Object.entries(WEIGHTS)
    .reduce((sum, [key, w]) => sum + metrics[key] * w, 0);

  // Premium feel: a design only feels premium when nothing is weak —
  // the minimum metric drags the bonus down hard.
  const weakest = Math.min(...Object.values(metrics));
  const premiumFeel = clamp01(weighted * 0.6 + weakest * 0.4);

  const breakdown = Object.fromEntries(
    Object.entries(metrics).map(([k, v]) => [k, Math.round(v * 100)])
  );

  return {
    score: Math.round(weighted * 100),
    premiumFeel: Math.round(premiumFeel * 100),
    breakdown,
  };
};
